import { useEffect, useRef, useState } from "react";
import { useTranslator } from "@eo-locale/react";

import { useRouter } from "next/router";

import { useCycle } from "framer-motion";

import Cookies from "js-cookie";

import { useAppSelector, useAppDispatch } from "@/redux/store/hooks";

import { Box, useDisclosure, useToast } from "@/storybook-components";
import GridContent from "@/components/shared/layouts/ehq/panelled/grid-content";
import ContentDrawer from "../common/drawer";
import Ideas from "./ideas";
import Idea from "./idea";
import LeftNode from "./sidebar/left-node";
import {
  handleVote,
  handleUnVote,
  getVotingStatusWrapper
} from "./vote/manage-votes";
import { setTriggerPoint } from "@/redux/features/unverified-modal/slice";
import { setIdeaTransitionDrawer } from "@/redux/features/brainstormers/slice";
import useUnverifiedModal from "@/hooks/useUnverfiedModal";

interface TriggerPoint {
  projectId: string;
  toolId: string;
  postId: string;
  triggeredSource: string;
  isTriggeredPointActive: boolean;
}

export const defaultTriggerPoint: TriggerPoint = {
  projectId: "",
  toolId: "",
  postId: "",
  triggeredSource: "",
  isTriggeredPointActive: false
};

const Content = (): JSX.Element => {
  const { translate } = useTranslator();
  const router = useRouter();
  const dispatch = useAppDispatch();
  const [postId, setPostId] = useState();
  const [filterParams, setFilterParams] = useState({});
  const { brainstormer, ideas, idea, meta, ideaError, status } = useAppSelector(
    (state) => state.brainstormers
  );
  const { features } = useAppSelector((state) => state.features);
  const isNewCookieConsentEnabled = features?.newCookieConsentManager?.enabled;

  const {
    canContribute,
    visibilityMode,
    state: projectState
  } = useAppSelector((state) => state.project.project);
  const {
    name,
    description,
    tagList,
    id: brainstormerId,
    state: brainstormerState,
    votingLimit
  } = brainstormer;

  const projectId = useAppSelector((state) => state?.project?.project?.id);
  const { triggerPoint } = useAppSelector((state) => state.unverifiedModal);
  const [voteTriggerPoint, setVoteTriggerPoint] = useState(defaultTriggerPoint);
  const { onOpen, isOpen, onClose } = useDisclosure();
  const [sidebarState, toggleSidebar] = useCycle("expand", "collapse");
  const contentDrawerZIndex = isNewCookieConsentEnabled ? 1200 : 1000;

  const queryId = router.query?.id;
  const cookieValues = Cookies.get("trigger_point");
  let cookieTriggerPoint;
  if (cookieValues) {
    cookieTriggerPoint = JSON.parse(cookieValues);
  }

  useEffect(() => {
    if (queryId && !ideaError) {
      onOpen();
    }
  }, [onOpen, queryId, router, ideaError]);

  useEffect(() => {
    if (ideaError) {
      if (router.query.idea) {
        delete router.query.idea;
        router.replace(router, "", { shallow: true });
      }
      onClose();
    }
    if (status === "ideaUpdated" || status === "closingIdea") onClose();
  }, [ideaError, status]);

  useEffect(() => {
    setVoteTriggerPoint(triggerPoint);
  }, [triggerPoint]);

  /**
   * To check height of left panel content and
   * customize "view feed" button for mobile screens
   */
  const [parentHeight, setParentHeight] = useState(null);
  const contentRef = useRef(null);
  const height = contentRef?.current?.clientHeight;
  const isDescriptionLong = !!(height > parentHeight?.clientHeight - 140);

  /**
   * Voting section
   */
  const toast = useToast({
    isClosable: true,
    containerStyle: {
      marginBottom: "1 !important"
    }
  });
  const { votingStartAt, votingEndAt, allowUnverifiedParticipation } =
    brainstormer;

  const findCurrentVotedIdea = (id) => {
    const filterenIdeaInList = ideas.filter((idea) => idea?.id === id);
    if (filterenIdeaInList.length) {
      return filterenIdeaInList[0];
    }
    return idea;
  };
  const processVotes = (
    project = projectId,
    tool = brainstormerId,
    post = postId
  ) => {
    const filteredArray = findCurrentVotedIdea(post);
    if (filteredArray?.currentUserVoted) {
      handleUnVote(
        post,
        filteredArray,
        dispatch,
        project,
        tool,
        translate,
        toast,
        filterParams
      );
    } else {
      handleVote(
        post,
        filteredArray,
        dispatch,
        project,
        tool,
        translate,
        toast,
        filterParams,
        votingLimit
      );
    }
  };
  useUnverifiedModal(allowUnverifiedParticipation, (isProceedAllowed) => {
    if (isProceedAllowed) {
      if (
        (voteTriggerPoint.triggeredSource === "vote" &&
          voteTriggerPoint.isTriggeredPointActive === true) ||
        cookieTriggerPoint?.triggeredSource === "vote"
      ) {
        cookieTriggerPoint
          ? processVotes(
              cookieTriggerPoint.projectId,
              cookieTriggerPoint.toolId,
              cookieTriggerPoint.postId
            )
          : processVotes();
      }
      dispatch(
        setTriggerPoint({ ...triggerPoint, isTriggeredPointActive: false })
      );
      Cookies.remove("trigger_point");
    }
  });
  const handleVoteToast = (id: string, toastTitle: string): void => {
    if (!toast.isActive(id)) {
      toast({
        title: translate(`brainstormers.${toastTitle}`),
        id: id,
        status: "warning"
      });
    }
  };
  const checkVotingEligibility = (): boolean => {
    const voteConditions = [
      {
        condition: () =>
          projectState === "archived" || brainstormerState === "archived",
        toastTitle: "archivedError"
      },
      {
        condition: () => visibilityMode === "Panelled" && !canContribute,
        toastTitle: "notPartOfPannelledGroup"
      },
      {
        condition: () =>
          getVotingStatusWrapper(votingStartAt, votingEndAt) === "not started",
        toastTitle: "votingPeriodNotStarted"
      },
      {
        condition: () =>
          getVotingStatusWrapper(votingStartAt, votingEndAt) === "ended",
        toastTitle: "votingPeriodEnded"
      }
    ];

    for (const { condition, toastTitle } of voteConditions) {
      if (condition()) {
        handleVoteToast("restrict-vote", toastTitle);
        return false;
      }
    }
    return true;
  };

  const manageVotes = (id) => {
    if (checkVotingEligibility()) {
      setPostId(id);
      const triggerPoint = {
        projectId,
        toolId: brainstormerId,
        postId: id,
        triggeredSource: "vote",
        isTriggeredPointActive: true
      };
      Cookies.set("trigger_point", JSON.stringify(triggerPoint), {
        expires: 1,
        secure: true,
        sameSite: "Strict"
      });
      dispatch(setTriggerPoint(triggerPoint));
    }
  };

  const onCloseHandler = () => {
    dispatch(setIdeaTransitionDrawer("none"));
    const routerQuery = router.query;
    if (routerQuery) {
      if (routerQuery.idea) delete routerQuery.idea;
      if (routerQuery.comment) delete routerQuery.comment;
      router.replace(router, "", { shallow: true });
    }
    onClose();
  };

  /**
   * Main body - ideas content
   */
  const contentNode = () => {
    return (
      <Ideas
        count={meta.count}
        onOpen={onOpen}
        isOpen={isOpen}
        sidebarState={sidebarState}
        toggleSidebar={toggleSidebar}
        manageVotes={manageVotes}
        ideas={ideas}
        filterParams={setFilterParams}
      />
    );
  };

  return (
    <Box>
      <GridContent
        sidebarState={sidebarState}
        leftPanel={
          <LeftNode
            isOpen={isOpen}
            toggleSidebar={toggleSidebar}
            isDescriptionLong={isDescriptionLong}
            name={name}
            description={description}
            tagList={tagList}
          />
        }
        contentBody={contentNode()}
        setCurrentContext={setParentHeight}
      />
      <ContentDrawer
        zIndex={contentDrawerZIndex}
        isOpen={isOpen}
        onClose={onCloseHandler}
        onCloseComplete={() => {
          if (status === "editTriggered")
            dispatch(setIdeaTransitionDrawer("update"));
        }}
        header={translate("brainstormers.contribution")}
        content={
          <Idea
            manageVotes={manageVotes}
            onContributionDrawerClose={onCloseHandler}
          />
        }
        trapFocus
        footer={null}
      />
    </Box>
  );
};

export default Content;
